import React, { useEffect, useRef } from 'react'
import { X, FileText, Copy, AlertTriangle, Loader2, ExternalLink, Clapperboard } from 'lucide-react'
import { useDocument } from '../hooks/useRag'
import {
  detectDocKind,
  KIND_LABEL,
  buildBlocks,
  sourceLink,
  TranscriptView,
  SubtitlesView,
  MarkdownView,
} from './docFormat'
import { canEmbed } from './VideoPlayer'
import usePlayerStore from '../stores/playerStore'

function basename(p) {
  if (!p) return 'Document'
  const parts = String(p).split(/[\\/]/)
  return parts[parts.length - 1] || p
}

export default function DocumentViewer({ project, source, highlightChunkIndex, asOf, onClose }) {
  const q = useDocument(project, source)
  const data = q.data
  const markRef = useRef(null)
  const openPlayer = usePlayerStore((s) => s.openPlayer)

  // Citation staleness (agent-user-gaps #4): the answer this viewer was opened
  // from carries its timestamp (asOf); the doc carries indexed_at. If the doc
  // was re-indexed after the answer, the citation may point elsewhere now.
  const reindexedSince =
    asOf && data?.indexed_at && Date.parse(data.indexed_at) > Date.parse(asOf)

  // Close on Esc.
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Scroll the highlighted chunk into view once loaded.
  useEffect(() => {
    if (data && markRef.current) {
      markRef.current.scrollIntoView({ block: 'center' })
    }
  }, [data])

  const span =
    highlightChunkIndex != null && data?.spans
      ? data.spans.find((s) => s.chunk_index === highlightChunkIndex)
      : null

  let body
  if (q.isLoading) {
    body = (
      <div className="flex items-center justify-center gap-2 py-16 text-ink-soft dark:text-mist/60">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading document…
      </div>
    )
  } else if (q.isError) {
    body = (
      <div className="m-5 rounded-lg border border-red-300/70 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800/60 dark:bg-red-900/20 dark:text-red-300">
        <div className="flex items-center gap-2 font-medium">
          <AlertTriangle className="w-4 h-4" /> {q.error?.message}
        </div>
        {q.error?.hint && <p className="mt-1 text-xs opacity-80">{q.error.hint}</p>}
      </div>
    )
  } else if (data) {
    const text = data.text || ''
    const kind = detectDocKind(source, text)
    if (kind === 'subtitles') {
      body = (
        <SubtitlesView
          blocks={buildBlocks(data, { stripHeaders: true })}
          highlightChunkIndex={highlightChunkIndex}
          markRef={markRef}
          docMeta={data}
        />
      )
    } else if (kind === 'transcript') {
      body = (
        <TranscriptView
          blocks={buildBlocks(data)}
          highlightChunkIndex={highlightChunkIndex}
          markRef={markRef}
        />
      )
    } else if (kind === 'markdown' && highlightChunkIndex == null) {
      // With a highlight requested we fall through to the plain view — a <mark>
      // can't be spliced into rendered markdown at a character offset.
      body = <MarkdownView text={text} />
    } else {
      body = (
        <div className="whitespace-pre-wrap break-words px-6 py-5 text-[14px] leading-relaxed text-ink/90 dark:text-mist/90 font-sans">
          {span ? (
            <>
              {text.slice(0, span.start)}
              <mark
                ref={markRef}
                className="rounded bg-gold/25 text-ink dark:bg-gold/30 dark:text-mist px-0.5 shadow-[0_0_0_1px_rgba(201,162,39,0.4)]"
              >
                {text.slice(span.start, span.end)}
              </mark>
              {text.slice(span.end)}
            </>
          ) : (
            text
          )}
        </div>
      )
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8 bg-midnight-900/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[86vh] flex flex-col rounded-xl border border-gold/40 bg-parchment-50 dark:bg-midnight-800 dark:border-gold/30 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center gap-3 px-5 py-3 border-b border-parchment-border dark:border-midnight-600">
          <FileText className="w-4 h-4 shrink-0 text-gold-deep dark:text-gold" />
          <div className="min-w-0 flex-1">
            <div className="font-display text-base text-ink dark:text-mist truncate" title={source}>
              {basename(source)}
            </div>
            {data && (
              <div className="text-[11px] text-ink-soft dark:text-mist/50 truncate" title={source}>
                {KIND_LABEL[detectDocKind(source, data.text)]} · {data.chunks} chunks
                {data.language ? ` · ${data.language}` : ''} · {source}
              </div>
            )}
          </div>
          {data && canEmbed(data) && (
            <button
              onClick={() =>
                openPlayer({
                  source_url: data.source_url,
                  origin: data.origin,
                  video_id: data.video_id,
                  title: data.title || data.video_name || basename(source),
                  // start at the referenced chunk's moment when we have one
                  start: span?.start_time ?? 0,
                })
              }
              className="p-2 rounded-md text-ink-soft hover:bg-parchment-200 dark:text-mist/60 dark:hover:bg-midnight-700"
              aria-label="Watch the source"
              title={span?.start_time_formatted ? `Watch the source from ${span.start_time_formatted}` : 'Watch the source video'}
            >
              <Clapperboard className="w-4 h-4" />
            </button>
          )}
          {data && !canEmbed(data) && sourceLink(data) && (
            <a
              href={sourceLink(data)}
              target="_blank"
              rel="noreferrer"
              className="p-2 rounded-md text-ink-soft hover:bg-parchment-200 dark:text-mist/60 dark:hover:bg-midnight-700"
              aria-label="Open source"
              title={`Open the source (${data.origin || 'external'})`}
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
          {data?.text && (
            <button
              onClick={() => navigator.clipboard?.writeText(data.text)}
              className="p-2 rounded-md text-ink-soft hover:bg-parchment-200 dark:text-mist/60 dark:hover:bg-midnight-700"
              aria-label="Copy text"
              title="Copy text"
            >
              <Copy className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-2 rounded-md text-ink-soft hover:bg-parchment-200 dark:text-mist/60 dark:hover:bg-midnight-700"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>
        {reindexedSince && (
          <div className="mx-5 mt-3 rounded-lg border border-gold/50 bg-gold/10 px-3 py-2 text-xs text-gold-deep dark:text-gold flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            Tento dokument byl přeindexován později, než vznikla odpověď, ze které sem odkazuješ — zvýrazněná pasáž nemusí přesně odpovídat citaci.
          </div>
        )}
        <div className="overflow-y-auto">{body}</div>
      </div>
    </div>
  )
}
